/**
 * Span of control.
 *
 * A point-in-time measure at the panel's own date — not a window — and it moves
 * with the basis toggle, because "average span" means something very different
 * once invigilation and marking pools are counted as reports. The server returns
 * the basis note; it is rendered verbatim rather than restated here.
 *
 * WITHHELD IS NOT ABSENT, AND NEITHER IS ZERO
 *  - null: withheld under the small-group rule. Drawn as a hatched track with a
 *    dash, and never taken into a maximum, a sum or a sentence.
 *  - a key left out: not carried for this date (a copy stored before the
 *    reporting-line rework has no reports, range, coverage or stand-in reading).
 *    The panel says so rather than going quiet.
 *  - nothing withheld is worked back here. The people outside the figures add up
 *    to the headcount less the reports, which is why the server withholds those
 *    groups together, and why no residual is ever printed on this panel.
 */
import React, { type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  INK,
  Def,
  fmtDay,
  fmtNum,
  fmtOne,
  panelDateLabel,
  type DefLookup,
  type ManagersOfManagersKey,
  type WorkforceBasis,
  type WorkforcePanels,
  type WorkforceSpan,
  type WorkforceSpanBand,
  type WorkforceSpanGroup,
} from './apiContract';

interface SpanPanelProps {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
  /**
   * The date the user picked. Span is the only panel here that is genuinely
   * point-in-time ON THE FRAME, so it is the only one whose figure can silently
   * belong to a different date than the one selected. When they differ, say so
   * here rather than in a page-level banner — a banner has to generalise across
   * panels whose windows differ, and generalising made it wrong for three of the
   * four it named.
   */
  selectedAsAt?: string | null;
}

// =============================================================================
// READING A SERVED FIGURE
// =============================================================================

const NOT_AVAILABLE = 'Not available for this date';

const WITHHELD_NOTE =
  'Withheld to protect small groups: fewer than five managers, or withheld alongside such a group so that '
  + 'a total cannot be used to work it back. The row stays; only its figures are withheld, and they are not zero.';

/** A served figure: a number, withheld (null), or not carried for this date (left out). */
type Reading = { kind: 'shown'; n: number } | { kind: 'withheld' } | { kind: 'absent' };

const reading = (v: number | null | undefined): Reading =>
  v === undefined ? { kind: 'absent' } : v === null ? { kind: 'withheld' } : { kind: 'shown', n: v };

/** Maximum over published values only — a withheld figure never reaches Math.max. */
const publishedMax = (values: (number | null | undefined)[]) =>
  Math.max(0, ...values.filter((v): v is number => typeof v === 'number'));

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const joinAnd = (items: string[]) =>
  items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const isWithheldGroup = (g: WorkforceSpanGroup) => g.suppressed || g.managers === null;

// =============================================================================
// WHO IS OUTSIDE THE FIGURES — one sentence per reading, built from the data.
// A zero drops its clause; a withheld group is named as withheld, never counted;
// a group the date does not carry is named as not available.
// =============================================================================

const VACANT_GROUP = 'reporting to manager positions that are vacant';
const LINE_GROUP = 'with no reporting line or a line outside the establishment';
const STAND_IN_LEAD =
  "Counting stand-in leave approvers as the managers of people whose manager's position is vacant";

function coverageSentences(span: WorkforceSpan): string[] {
  const cov = span.coverage;
  const si = span.with_stand_ins;
  if (!cov && !si) {
    return [
      'Who is outside these figures, and the same figures counting stand-in leave approvers, are not available for this date.',
    ];
  }
  const out: string[] = [];

  if (!cov) {
    out.push('Who is outside these figures is not available for this date.');
  } else {
    const vacant = reading(cov.reporting_to_vacant_seat);
    const positions = reading(cov.vacant_seats_with_reports);
    const noLine = reading(cov.without_line);
    const outside = reading(cov.line_outside_establishment);

    const shown: string[] = [];
    if (vacant.kind === 'shown' && vacant.n > 0) {
      const to =
        positions.kind === 'shown' && positions.n > 0
          ? `${fmtNum(positions.n)} manager ${plural(positions.n, 'position that is', 'positions that are')} vacant`
          : 'manager positions that are vacant';
      shown.push(`${fmtNum(vacant.n)} ${plural(vacant.n, 'person reports', 'people report')} to ${to}`);
    }
    if (noLine.kind === 'shown' && noLine.n > 0) {
      shown.push(`${fmtNum(noLine.n)} ${plural(noLine.n, 'has', 'have')} no reporting line`);
    }
    if (outside.kind === 'shown' && outside.n > 0) {
      shown.push(
        `${fmtNum(outside.n)} ${plural(outside.n, 'reports', 'report')} to a position outside the establishment`,
      );
    }
    if (shown.length) out.push(`${joinAnd(shown)}, so they are outside these figures.`);

    const vacantWithheld = vacant.kind === 'withheld';
    const lineWithheld = noLine.kind === 'withheld' || outside.kind === 'withheld';
    if (vacantWithheld && lineWithheld) {
      out.push(
        `People ${VACANT_GROUP}, and people ${LINE_GROUP}, are outside these figures; how many is withheld to protect small groups.`,
      );
    } else if (vacantWithheld || lineWithheld) {
      const group = vacantWithheld ? VACANT_GROUP : LINE_GROUP;
      out.push(
        shown.length
          ? `The number of people ${group} is withheld to protect small groups.`
          : `People ${group} are outside these figures; how many is withheld to protect small groups.`,
      );
    }

    const absent = [
      vacant.kind === 'absent' ? VACANT_GROUP : null,
      noLine.kind === 'absent' && outside.kind === 'absent' ? LINE_GROUP : null,
    ].filter((g): g is string => g !== null);
    if (absent.length) {
      out.push(`The number of people ${absent.join(', or ')} is not available for this date.`);
    }
  }

  if (!si) {
    out.push('The same figures counting stand-in leave approvers as managers are not available for this date.');
  } else {
    const managers = reading(si.managers);
    const avg = reading(si.avg_span);
    if (managers.kind !== 'shown') {
      out.push(
        `${STAND_IN_LEAD}: ${managers.kind === 'withheld' ? 'withheld to protect small groups' : 'not available for this date'}.`,
      );
    } else {
      const head = `${STAND_IN_LEAD} gives ${fmtNum(managers.n)} ${plural(managers.n, 'manager', 'managers')}`;
      out.push(
        avg.kind === 'shown'
          ? `${head} and an average span of ${fmtOne(avg.n)}.`
          : avg.kind === 'withheld'
            ? `${head}; their average span is withheld to protect small groups.`
            : `${head}; their average span is not available for this date.`,
      );
    }
  }
  return out;
}

// =============================================================================
// ROWS — label / bar / figures, with a hatched track for a withheld row. The
// house BarRow draws a null as an empty track, which reads as a bar of length
// zero; here a withheld row must never be mistaken for an empty one.
// =============================================================================

const TRACK = '#EEF1F6';
const WITHHELD_FILL = 'repeating-linear-gradient(45deg, #C3CDDD 0 3px, #EDF1F7 3px 6px)';
const FIGURE_COL = '58px';

const gridColumns = (labelWidth: number, figures: number) =>
  `minmax(0, ${labelWidth}px) 1fr ${Array.from({ length: figures }, () => FIGURE_COL).join(' ')}`;

/** A withheld figure is NOT a zero, and it carries the rule that withheld it. */
const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    —
    <Def def={def} k="suppression" text={WITHHELD_NOTE} />
  </span>
);

const FigureRow: React.FC<{
  label: string;
  /** Drives the bar; ignored when the row is withheld. */
  value: number | null | undefined;
  withheld: boolean;
  max: number;
  color: string;
  figures: ReactNode[];
  labelWidth: number;
}> = ({ label, value, withheld, max, color, figures, labelWidth }) => {
  const pct =
    !withheld && typeof value === 'number' && value > 0 && max > 0 ? Math.max(0.8, (value / max) * 100) : 0;
  const withheldText = `${label}: withheld to protect small groups`;
  return (
    <div
      className="grid items-center gap-2.5 py-[3px] text-[12.5px]"
      style={{ gridTemplateColumns: gridColumns(labelWidth, figures.length) }}
    >
      <div className="truncate text-gray-700" title={label}>
        {label}
      </div>
      <div className="relative h-[13px] overflow-hidden rounded-sm" style={{ background: TRACK }}>
        {withheld ? (
          <span
            role="img"
            aria-label={withheldText}
            title={withheldText}
            className="absolute inset-0"
            style={{ background: WITHHELD_FILL }}
          />
        ) : pct > 0 ? (
          <span
            className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-300"
            style={{ width: `${pct}%`, background: color }}
          />
        ) : null}
      </div>
      {figures.map((f, i) => (
        <div key={i} className="text-right font-semibold tabular-nums text-heritage-blue">
          {f}
        </div>
      ))}
    </div>
  );
};

const SectionHead: React.FC<{
  title: string;
  defKey: string;
  def: DefLookup;
  caption?: string;
  withheld: boolean;
  heads?: string[];
  labelWidth?: number;
}> = ({ title, defKey, def, caption, withheld, heads, labelWidth = 120 }) => (
  <>
    <div className="mb-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
        <Def def={def} k={defKey} />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 text-[11px] text-gray-500">
        {caption ? <span>{caption}</span> : null}
        {withheld ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: WITHHELD_FILL }} />
            withheld
            <Def def={def} k="suppression" text={WITHHELD_NOTE} />
          </span>
        ) : null}
      </div>
    </div>
    {heads ? (
      <div
        className="grid gap-2.5 text-[10px] uppercase tracking-wide text-gray-400"
        style={{ gridTemplateColumns: gridColumns(labelWidth, heads.length) }}
      >
        <span />
        <span />
        {heads.map((h) => (
          <span key={h} className="text-right">
            {h}
          </span>
        ))}
      </div>
    ) : null}
  </>
);

// =============================================================================
// GROUPS — grade bands, manager roles, managers of managers (employee basis)
// =============================================================================

const BAND_ORDER = ['F', 'E', 'D', 'C', 'B', 'NG'];

const orderBands = (rows: WorkforceSpanBand[]) =>
  rows
    .map((row, i) => {
      const at = BAND_ORDER.indexOf(row.band);
      return { row, rank: at < 0 ? BAND_ORDER.length + i : at };
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ row }) => row);

const bandLabel = (band: string) => (band === 'NG' ? 'Not graded' : `Band ${band}`);

const MANAGERS_OF_MANAGERS: { key: ManagersOfManagersKey; label: string }[] = [
  { key: 'manage_managers', label: 'Manage managers' },
  { key: 'manage_individual_contributors', label: 'Manage individual contributors' },
];

const spanRange = (min: number | null | undefined, max: number | null | undefined): string =>
  typeof min === 'number' && typeof max === 'number'
    ? min === max
      ? fmtNum(max)
      : `${fmtNum(min)}–${fmtNum(max)}`
    : '—';

/** Managers, average and (optionally) range. A group with no managers has no average or range. */
function groupFigures(g: WorkforceSpanGroup, def: DefLookup, withRange: boolean): ReactNode[] {
  if (isWithheldGroup(g)) return [<Withheld def={def} />, '—', ...(withRange ? ['—'] : [])];
  const managers = g.managers === undefined ? <span title={NOT_AVAILABLE}>—</span> : fmtNum(g.managers);
  return [managers, fmtOne(g.avg_span), ...(withRange ? [spanRange(g.min_span, g.max_span)] : [])];
}

const managersForBar = (g: WorkforceSpanGroup) => (isWithheldGroup(g) ? null : g.managers);

const NOTE_BOX = 'rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500';

// =============================================================================
// PANEL
// =============================================================================

export const SpanPanel: React.FC<SpanPanelProps> = ({ panels, basis, loading, def, selectedAsAt }) => {
  const span = panels?.span ?? null;
  const employees = basis === 'employees';
  // The one genuine mismatch on this page: span is point-in-time on the frame,
  // so selecting an earlier period shows reporting lines from a later date.
  const offPeriod = Boolean(
    panels?.panels_as_at && selectedAsAt && panels.panels_as_at !== selectedAsAt,
  );
  // Dated from the block's own vintage, never panels_as_at: that advances every
  // night whether or not span was recomputed.
  const dateLabel = panelDateLabel(panels, employees ? 'span_employees' : 'span_all');

  const distEntries = Object.entries(span?.distribution ?? {});
  const maxDist = publishedMax(distEntries.map(([, v]) => v));
  const distWithheld = distEntries.some(([, v]) => v === null);

  const bands = orderBands(span?.by_band ?? []);
  const bandMax = publishedMax(bands.map(managersForBar));
  const roles = span?.by_manager_role ?? [];
  const roleMax = publishedMax(roles.map(managersForBar));
  const splits = MANAGERS_OF_MANAGERS.flatMap(({ key, label }) => {
    const group = span?.managers_of_managers?.[key];
    return group ? [{ key, label, group }] : [];
  });
  const splitMax = publishedMax(splits.map(({ group }) => managersForBar(group)));
  const notCarried = [
    bands.length ? null : 'span by grade band',
    roles.length ? null : 'the largest manager roles',
    splits.length ? null : 'managers of managers',
  ].filter((s): s is string => s !== null);

  const hasMin = typeof span?.min_span === 'number';

  return (
    <PanelShell
      title="Span of control"
      defKey="span"
      def={def}
      window={`${dateLabel} — reporting lines as they stand, not a window.${
        offPeriod
          ? ` You have ${fmtDay(selectedAsAt)} selected; reporting lines are held only for the latest date, so these are the nearest held.`
          : ''
      } Follows the basis toggle.`}
      footnote={
        span?.basis_note ??
        (employees
          ? 'Managers with at least one Permanent or fixed-term report; sessional reports excluded.'
          : 'All filled reporting lines, sessional included — which is why the average widens: marking and invigilation pools report into a handful of academic managers.')
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={220} />
      ) : !panels ? (
        <PanelUnavailable what="Span of control" />
      ) : !span ? (
        <div className={NOTE_BOX}>Span of control is not available for this date.</div>
      ) : (
        <div className="space-y-4">
          <TileRow min="112px">
            <MetricTile label="Managers" value={fmtNum(span.managers)} defKey="span_managers" def={def} size="sm" />
            <MetricTile
              label="Reports"
              value={fmtNum(span.reports)}
              sub={span.reports === undefined ? NOT_AVAILABLE : undefined}
              defKey="span_reports"
              def={def}
              size="sm"
            />
            <MetricTile label="Average span" value={fmtOne(span.avg_span)} defKey="avg_span" def={def} size="sm" />
            <MetricTile label="Median" value={fmtOne(span.median_span)} defKey="median_span" def={def} size="sm" />
            <MetricTile
              label="Range"
              value={hasMin ? spanRange(span.min_span, span.max_span) : fmtNum(span.max_span)}
              sub={hasMin ? undefined : 'Widest only; the smallest is not available for this date'}
              defKey="span_range"
              def={def}
              size="sm"
            />
          </TileRow>

          <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11.5px] leading-snug text-gray-600">
            {coverageSentences(span).join(' ')}
            <Def def={def} k="span_coverage" />
          </div>

          {distEntries.length ? (
            <div>
              <SectionHead
                title="Managers by number of reports"
                defKey="span_distribution"
                def={def}
                withheld={distWithheld}
              />
              {distEntries.map(([bucket, count]) => (
                <FigureRow
                  key={bucket}
                  label={`${bucket} reports`}
                  value={count}
                  withheld={count === null}
                  max={maxDist}
                  color={INK}
                  figures={[count === null ? <Withheld def={def} /> : fmtNum(count)]}
                  labelWidth={120}
                />
              ))}
            </div>
          ) : (
            <div className={NOTE_BOX}>
              {employees
                ? 'The distribution of managers by number of reports is not available for this date.'
                : 'No distribution is published on this basis — only the summary figures above.'}
            </div>
          )}

          {employees ? (
            <>
              {bands.length ? (
                <div>
                  <SectionHead
                    title="By grade band"
                    defKey="span_by_band"
                    def={def}
                    caption="Grades as the Role Master holds them today"
                    withheld={bands.some(isWithheldGroup)}
                    heads={['Managers', 'Average', 'Range']}
                  />
                  {bands.map((g) => (
                    <FigureRow
                      key={g.band}
                      label={bandLabel(g.band)}
                      value={managersForBar(g)}
                      withheld={isWithheldGroup(g)}
                      max={bandMax}
                      color={INK}
                      figures={groupFigures(g, def, true)}
                      labelWidth={120}
                    />
                  ))}
                </div>
              ) : null}

              {roles.length ? (
                <div>
                  <SectionHead
                    title="Largest manager roles"
                    defKey="span_by_manager_role"
                    def={def}
                    withheld={roles.some(isWithheldGroup)}
                    heads={['Managers', 'Average', 'Range']}
                    labelWidth={210}
                  />
                  {roles.map((g) => (
                    <FigureRow
                      key={g.title}
                      label={g.title}
                      value={managersForBar(g)}
                      withheld={isWithheldGroup(g)}
                      max={roleMax}
                      color={ACCENT}
                      figures={groupFigures(g, def, true)}
                      labelWidth={210}
                    />
                  ))}
                </div>
              ) : null}

              {splits.length ? (
                <div>
                  <SectionHead
                    title="Managers of managers"
                    defKey="managers_of_managers"
                    def={def}
                    withheld={splits.some(({ group }) => isWithheldGroup(group))}
                    heads={['Managers', 'Average']}
                    labelWidth={190}
                  />
                  {splits.map(({ key, label, group }) => (
                    <FigureRow
                      key={key}
                      label={label}
                      value={managersForBar(group)}
                      withheld={isWithheldGroup(group)}
                      max={splitMax}
                      color={INK}
                      figures={groupFigures(group, def, false)}
                      labelWidth={190}
                    />
                  ))}
                </div>
              ) : null}

              {notCarried.length ? (
                <div className="text-[11px] leading-snug text-gray-500">
                  {capitalise(joinAnd(notCarried))} {notCarried.length === 1 ? 'is' : 'are'} not available
                  for this date.
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-[11px] leading-snug text-gray-500">
              The grade band and manager role breakdowns count employee reports only, so they are shown on
              the Employees basis.
            </div>
          )}
        </div>
      )}
    </PanelShell>
  );
};
